/**
 * Estado do editor do estudo "Por que consórcio" (PorQueConsorcioDialog):
 * premissas, plano usado no estudo, outros planos e seções ligadas. O estudo
 * é recalculado a cada escolha, e o PDF e o link saem exatamente dele.
 *
 * As premissas nascem do mercado (premissasMercado.ts) e podem ser trocadas.
 * O plano nasce da proposta; "Usar" troca por outra categoria ou grupo com
 * crédito parecido (alternativas.ts), refazendo a simulação com as regras
 * daquele grupo.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNotify } from 'ra-core';
import { anualDeMensal, mensalDeAnual, type Segmento } from './comparativo';
import { AGIO_PADRAO_PCT, descobrirSegmento, entradaDoPayload, jurosDeMercado } from './comparativoPdf';
import { PADRAO, carregarIndicadores, type Indicadores } from './indicadoresBcb';
import { cenarioDoPayload, montarEstudo, simulacaoDoPayload, type Estudo, type OpcoesEstudo } from './estudo';
import type { SecaoId } from './estudoPaginas';
import {
  aluguelDeMercado,
  cdiDeMercado,
  jurosFinanciamentoDeMercado,
  opcoesDoEstudo,
  reajusteDeMercado,
  usePremissa,
  valorizacaoDeMercado,
} from './premissasMercado';
import type { CenarioResumo } from './resumoCliente';
import type { ProposalPayload } from './proposal-utils';
import {
  chaveAlternativa,
  payloadDaAlternativa,
  ranquearAlternativas,
  selecaoInicial,
  type Alternativa,
  type Candidato,
  type OrdemAlternativas,
} from './alternativas';
import { buscarCandidatos, carregarDetalhesGrupo } from './alternativasDados';

export type ModoJuros = 'mercado' | 'ano' | 'mes';

/** "1,5", "1.5" e "1.234,5" → número; vazio ou lixo → null. */
export const lerNumero = (texto: string): number | null => {
  const t = texto.trim().replace('%', '').trim();
  if (t === '') return null;
  // Com vírgula, ela é o decimal e os pontos são milhar; sem vírgula, o ponto é o decimal.
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) ? n : null;
};

const paraCampo = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export const useEstudoEditor = (payload: ProposalPayload | null) => {
  const notify = useNotify();
  const [ind, setInd] = useState<Indicadores | null>(null);
  const [segmento, setSegmento] = useState<Segmento | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [modo, setModo] = useState<ModoJuros>('mercado');
  const [jurosTexto, setJurosTexto] = useState('');
  const [mesTexto, setMesTexto] = useState('');
  const [agioTexto, setAgioTexto] = useState(String(AGIO_PADRAO_PCT));
  const [cenario, setCenario] = useState<CenarioResumo>('sorteio');
  // Plano usado no estudo quando o vendedor troca; null = o da proposta.
  const [payloadAtivo, setPayloadAtivo] = useState<ProposalPayload | null>(null);
  const [usando, setUsando] = useState<string | null>(null);
  const [candidatos, setCandidatos] = useState<Candidato[] | null>(null);
  const [falhouAlternativas, setFalhouAlternativas] = useState(false);
  const [ordem, setOrdem] = useState<OrdemAlternativas>('economia');
  const [selecionadas, setSelecionadas] = useState<Set<string> | null>(null);
  const [ocultas, setOcultas] = useState<SecaoId[]>([]);

  // Antes de o BCB responder valem as premissas de referência (PADRAO).
  const base = ind ?? { ...PADRAO, fonte: 'padrao' as const };
  const seg = segmento ?? 'veiculo';
  const reajuste = usePremissa(reajusteDeMercado(base, seg));
  const cdi = usePremissa(cdiDeMercado(base));
  const valorizacao = usePremissa(valorizacaoDeMercado(base));
  const aluguel = usePremissa(aluguelDeMercado());

  const ativo = payloadAtivo ?? payload;
  // A proposta do simulador traz a simulação inteira; a da North IA, não.
  const temSimulacao = ativo ? simulacaoDoPayload(ativo, 1) !== null : false;

  // Cada proposta aberta recomeça do padrão.
  useEffect(() => {
    if (!payload) return;
    let vivo = true;
    setModo('mercado');
    setJurosTexto('');
    setAgioTexto(String(AGIO_PADRAO_PCT));
    setMesTexto(String(Math.max(1, Math.round(Number(payload.mesesContemplacao) || 12))));
    setCenario(cenarioDoPayload(payload));
    setPayloadAtivo(null);
    setCandidatos(null);
    setFalhouAlternativas(false);
    setSelecionadas(null);
    setOcultas([]);
    reajuste.restaurar();
    cdi.restaurar();
    valorizacao.restaurar();
    aluguel.restaurar();
    setSegmento(null);
    setCarregando(true);
    Promise.all([carregarIndicadores(), descobrirSegmento(payload)])
      .then(([i, s]) => {
        if (!vivo) return;
        setInd(i);
        setSegmento(s);
      })
      .catch((err) => {
        console.error('Por que consórcio: falha ao carregar taxas', err);
        if (vivo) notify('Não foi possível carregar as taxas do Banco Central.', { type: 'warning' });
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
    // As premissas só voltam ao mercado quando a proposta muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, notify]);

  // Outros planos: pelo crédito por cota da proposta original.
  const cotas = payload ? parseInt(String(payload.juncaoCota ?? '1'), 10) || 1 : 1;
  const creditoPorCota = payload ? Number(payload.valorCreditoJunto ?? payload.valorCredito ?? 0) / cotas : 0;
  const grupoOriginal = payload && Number(payload.groupNumber) ? Number(payload.groupNumber) : null;
  useEffect(() => {
    if (!payload || !segmento || !(creditoPorCota > 0)) return;
    let vivo = true;
    buscarCandidatos(segmento, creditoPorCota, grupoOriginal)
      .then((c) => vivo && setCandidatos(c))
      .catch((err) => {
        console.error('Por que consórcio: falha ao buscar outros planos', err);
        if (vivo) setFalhouAlternativas(true);
      });
    return () => {
      vivo = false;
    };
  }, [payload, segmento, creditoPorCota, grupoOriginal]);

  const prazo = ativo && segmento ? entradaDoPayload(ativo, segmento)?.prazo ?? null : null;
  const mercado = ind && segmento ? jurosDeMercado(ind, segmento) : null;

  const juros = lerNumero(jurosTexto);
  const jurosAa: number | null | undefined =
    modo === 'mercado' ? null
      : juros === null || juros <= 0 ? undefined
      : modo === 'ano' ? (juros < 200 ? juros : undefined)
      : juros < 15 ? anualDeMensal(juros / 100) : undefined;
  const mes = lerNumero(mesTexto);
  const mesValido = mes !== null && Number.isInteger(mes) && mes >= 1 && (prazo === null || mes <= prazo);
  const agio = lerNumero(agioTexto);
  const agioValido = agio !== null && agio >= 0 && agio <= 100;

  const erro =
    jurosAa === undefined ? `Informe juros válidos ${modo === 'ano' ? 'ao ano' : 'ao mês'}.`
      : !mesValido ? `O mês de contemplação vai de 1 a ${prazo ?? 'o prazo'}.`
      : !agioValido ? 'O ágio vai de 0% a 100%.'
      : null;

  const jurosResolvido = jurosAa ?? (ind && segmento ? jurosFinanciamentoDeMercado(ind, segmento).valor : null);
  const sistema: 'SAC' | 'Price' = seg === 'imovel' ? 'SAC' : 'Price';

  const alternativas = useMemo<Alternativa[] | null>(() => {
    if (!candidatos || jurosResolvido === null) return null;
    return ranquearAlternativas(candidatos, {
      creditoPorCota,
      cotas,
      jurosAa: jurosResolvido,
      sistema,
      grupoAtual: ativo && Number(ativo.groupNumber) ? Number(ativo.groupNumber) : null,
      categoriaAtual: ativo?.categoria ? String(ativo.categoria) : null,
      prazoAtual: ativo && Number(ativo.prazo) ? Number(ativo.prazo) : null,
      ordem,
    });
  }, [candidatos, jurosResolvido, creditoPorCota, cotas, sistema, ativo, ordem]);

  // Na primeira lista: o plano do estudo e os melhores que ganham do
  // financiamento (selecaoInicial, a mesma da North IA ao gerar o link).
  useEffect(() => {
    if (selecionadas !== null || !alternativas) return;
    const inicial = selecaoInicial(alternativas);
    if (inicial.ordem) setOrdem(inicial.ordem);
    setSelecionadas(inicial.chaves);
  }, [alternativas, selecionadas]);

  const trocarModo = (novo: ModoJuros) => {
    setModo(novo);
    // Começa do valor de mercado no formato escolhido, para o vendedor ajustar.
    if (mercado && novo !== 'mercado') {
      setJurosTexto(paraCampo(novo === 'ano' ? mercado.valor : mensalDeAnual(mercado.valor) * 100));
    }
  };

  const alternarSelecao = (chave: string) =>
    setSelecionadas((s) => {
      const nova = new Set(s ?? []);
      if (nova.has(chave)) nova.delete(chave);
      else nova.add(chave);
      return nova;
    });

  const alternarSecao = (id: SecaoId) => setOcultas((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  /** Troca o plano do estudo pela alternativa, com as regras do grupo dela. */
  const usarAlternativa = async (alt: Alternativa) => {
    if (!payload || !segmento) return;
    const chave = chaveAlternativa(alt);
    if (alt.grupo === grupoOriginal && alt.categoria === String(payload.categoria ?? '') && alt.prazo === Number(payload.prazo)) {
      setPayloadAtivo(null);
      return;
    }
    setUsando(chave);
    try {
      const detalhes = await carregarDetalhesGrupo(alt.grupo);
      const novo = payloadDaAlternativa(payload, alt, detalhes, segmento);
      setPayloadAtivo(novo);
      setMesTexto(String(novo.mesesContemplacao));
      setSelecionadas((s) => new Set([...(s ?? []), chave]));
    } catch (err) {
      console.error('Por que consórcio: falha ao usar outro plano', err);
      notify('Não foi possível carregar as regras deste grupo.', { type: 'error' });
    } finally {
      setUsando(null);
    }
  };

  const voltarAoOriginal = () => {
    setPayloadAtivo(null);
    if (payload) setMesTexto(String(Math.max(1, Math.round(Number(payload.mesesContemplacao) || 12))));
  };

  // A mesma montagem que a North IA usa ao gerar o link (premissas.ts).
  const opcoes: OpcoesEstudo | null =
    segmento && ind && jurosAa !== undefined && mesValido && agioValido
      ? opcoesDoEstudo(ind, {
          segmento,
          jurosAa,
          mesContemplacao: mes as number,
          agioPct: agio as number,
          cdiAa: cdi.valor,
          reajusteAa: reajuste.valor,
          valorizacaoAa: valorizacao.valor,
          aluguelAm: aluguel.valor,
          cenario,
          alternativas: (alternativas ?? []).filter((a) => selecionadas?.has(chaveAlternativa(a))),
          secoesOcultas: ocultas,
        })
      : null;

  // O estudo do momento: o que a pré-visualização mostra, o PDF baixa e o link grava.
  const chave = JSON.stringify(opcoes);
  const estudo = useMemo<Estudo | null>(
    () => (ativo && opcoes ? montarEstudo(ativo, opcoes) : null),
    // opcoes é recriado a cada render; a chave é o conteúdo dele.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ativo, chave],
  );

  return {
    ind, segmento, setSegmento, carregando, mercado, prazo, temSimulacao,
    modo, trocarModo, jurosTexto, setJurosTexto, jurosAa,
    mesTexto, setMesTexto, agioTexto, setAgioTexto, cenario, setCenario,
    reajuste, cdi, valorizacao, aluguel, erro,
    ativo, trocouPlano: payloadAtivo !== null, voltarAoOriginal,
    alternativas, falhouAlternativas, ordem, setOrdem, sistema,
    selecionadas: selecionadas ?? new Set<string>(), alternarSelecao, usarAlternativa, usando,
    ocultas, alternarSecao,
    estudo,
  };
};

export type EstudoEditor = ReturnType<typeof useEstudoEditor>;
